import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

const SECTION_TYPES = ["wallet", "giftcard", "referral", "app_section", "credit_line"];

export default function FaqAdminEdit() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [loading, setLoading] = useState(true);
  const [savingTopic, setSavingTopic] = useState(false);
  const [savingNew, setSavingNew] = useState(false);

  const [topic, setTopic] = useState("");
  const [topicOrder, setTopicOrder] = useState(1);
  const [sectionType, setSectionType] = useState(SECTION_TYPES[0]);
  const [creditLineRestricted, setCreditLineRestricted] = useState(false);

  const [rows, setRows] = useState([]);
  const [newRows, setNewRows] = useState([{ question: "", answer: "", sort_order: "" }]);

  const fetchTopic = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/faqs/${slug}`);
      const data = res.data?.data;

      setTopic(data?.topic || "");
      setTopicOrder(data?.topic_order || 1);
      setSectionType(data?.section_type || SECTION_TYPES[0]);
      setCreditLineRestricted(!!data?.credit_line_restricted);

      setRows(
        (data?.faqs || []).map((r) => ({
          faq_id: r.faq_id,
          question: r.question || "",
          answer: r.answer || "",
          sort_order: r.sort_order || 1,
        }))
      );
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load topic");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopic();
  }, [slug]);

  const saveTopic = async () => {
    try {
      setSavingTopic(true);

      const res = await api.patch(`/admin/faqs/topic/${slug}`, {
        topic,
        topic_order: Number(topicOrder) || 1,
        section_type: sectionType,
        credit_line_restricted: sectionType === "credit_line" ? creditLineRestricted : false,
      });

      toast.success("Topic updated");

      const newSlug = res.data?.data?.newSlug;
      if (newSlug && newSlug !== slug) {
        navigate(`/admin/faqs/edit/${newSlug}`);
        return;
      }

      fetchTopic();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Update failed");
    } finally {
      setSavingTopic(false);
    }
  };

  const updateRowLocal = (faq_id, key, value) => {
    setRows((prev) =>
      prev.map((r) => (r.faq_id === faq_id ? { ...r, [key]: value } : r))
    );
  };

  const saveItem = async (faq_id) => {
    const row = rows.find((r) => r.faq_id === faq_id);
    if (!row) return;

    if (!row.question.trim() || !row.answer.trim()) {
      return toast.error("Question & Answer cannot be empty");
    }

    try {
      await api.patch(`/admin/faqs/item/${faq_id}`, {
        question: row.question,
        answer: row.answer,
        sort_order: Number(row.sort_order) || 1,
      });
      toast.success("Saved");
      fetchTopic();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Save failed");
    }
  };

  const deactivateItem = async (faq_id) => {
    if (!window.confirm("Deactivate this question?")) return;
    try {
      await api.delete(`/admin/faqs/item/${faq_id}`);
      toast.success("Question deactivated");
      fetchTopic();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed");
    }
  };

  const addNewRow = () => {
    setNewRows((p) => [...p, { question: "", answer: "", sort_order: "" }]);
  };

  const removeNewRow = (idx) => {
    if (newRows.length === 1) return;
    setNewRows((p) => p.filter((_, i) => i !== idx));
  };

  const updateNewRow = (idx, key, value) => {
    setNewRows((p) => p.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const saveNewQuestions = async () => {
    const validRows = newRows.filter((r) => r.question.trim() && r.answer.trim());
    if (validRows.length === 0) return toast.error("Fill at least one new question");

    try {
      setSavingNew(true);

      await api.post(`/admin/faqs/${slug}/add-bulk`, {
        questions: validRows.map((r) => ({
          question: r.question,
          answer: r.answer,
          sort_order: r.sort_order ? Number(r.sort_order) : undefined,
        })),
      });

      toast.success("New questions added");
      setNewRows([{ question: "", answer: "", sort_order: "" }]);
      fetchTopic();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to add");
    } finally {
      setSavingNew(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Edit FAQ Topic</h1>
        <button
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start"
          onClick={() => navigate("/admin/faqs")}
          type="button"
        >
          Back
        </button>
      </div>

      {loading ? (
        <div className="mt-6 text-sm text-gray-600">Loading...</div>
      ) : (
        <div className="mt-6 space-y-6">
          {/* Topic */}
          <div className="border rounded-2xl p-5 space-y-3">
            <div className="font-semibold">Topic Details</div>

            <div>
              <label className="text-sm font-semibold">Topic</label>
              <input
                className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
            </div>

            <div className="max-w-[200px]">
              <label className="text-sm font-semibold">Topic Order</label>
              <input
                type="number"
                className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                value={topicOrder}
                onChange={(e) => setTopicOrder(e.target.value)}
              />
            </div>

            <div className="max-w-[200px]">
              <label className="text-sm font-semibold">Section Type</label>
              <select
                className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                value={sectionType}
                onChange={(e) => setSectionType(e.target.value)}
              >
                {SECTION_TYPES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {sectionType === "credit_line" && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="creditLineRestrictedEdit"
                  checked={creditLineRestricted}
                  onChange={(e) => setCreditLineRestricted(e.target.checked)}
                />
                <label htmlFor="creditLineRestrictedEdit" className="text-sm font-semibold">
                  Visible only to users with credit line enabled
                </label>
              </div>
            )}

            <button
              type="button"
              onClick={saveTopic}
              disabled={savingTopic}
              className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {savingTopic ? "Saving..." : "Save Topic"}
            </button>
          </div>

          {/* Existing */}
          <div className="font-semibold">Existing Questions</div>

          <div className="space-y-3">
            {rows.map((r, idx) => (
              <div key={r.faq_id} className="border rounded-2xl p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="text-sm font-bold">Q{idx + 1}</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => saveItem(r.faq_id)}
                      className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => deactivateItem(r.faq_id)}
                      className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                    >
                      Deactivate
                    </button>
                  </div>
                </div>

                <input
                  className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  value={r.question}
                  onChange={(e) => updateRowLocal(r.faq_id, "question", e.target.value)}
                />

                <textarea
                  className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  rows={4}
                  value={r.answer}
                  onChange={(e) => updateRowLocal(r.faq_id, "answer", e.target.value)}
                />

                <div className="max-w-[200px]">
                  <label className="text-xs font-semibold">Sort Order</label>
                  <input
                    type="number"
                    className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                    value={r.sort_order}
                    onChange={(e) => updateRowLocal(r.faq_id, "sort_order", e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Add new */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3">
            <div className="font-semibold">Add New Questions</div>
            <button
              type="button"
              onClick={addNewRow}
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start"
            >
              + Add Question
            </button>
          </div>

          <div className="space-y-3">
            {newRows.map((q, idx) => (
              <div key={idx} className="border rounded-2xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">New Q{idx + 1}</div>
                  <button
                    type="button"
                    onClick={() => removeNewRow(idx)}
                    className="text-xs font-semibold border rounded-lg px-3 py-1 hover:bg-gray-50"
                  >
                    Remove
                  </button>
                </div>

                <input
                  className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  placeholder="Question"
                  value={q.question}
                  onChange={(e) => updateNewRow(idx, "question", e.target.value)}
                />

                <textarea
                  className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  rows={4}
                  placeholder="Answer"
                  value={q.answer}
                  onChange={(e) => updateNewRow(idx, "answer", e.target.value)}
                />

                <div className="max-w-[200px]">
                  <label className="text-xs font-semibold">Sort Order (Optional)</label>
                  <input
                    type="number"
                    className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                    placeholder="Auto"
                    value={q.sort_order}
                    onChange={(e) => updateNewRow(idx, "sort_order", e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={saveNewQuestions}
            disabled={savingNew}
            className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {savingNew ? "Saving..." : "Save New Questions"}
          </button>
        </div>
      )}
    </div>
  );
}
